import { useEffect, useState } from "react";
import ClientLayout from "@/components/ClientLayout";
import api from "@/lib/api";
import { ProvenanceBadge } from "@/components/Badge";
import { Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  { key: "poids", label: "Poids (kg)" },
  { key: "tour_taille", label: "Tour de taille (cm)" },
  { key: "tour_hanches", label: "Tour de hanches (cm)" },
  { key: "tour_poitrine", label: "Tour de poitrine (cm)" },
  { key: "tour_bras", label: "Tour de bras (cm)" },
  { key: "tour_cuisses", label: "Tour de cuisses (cm)" },
];

export default function Measurements() {
  const [doc, setDoc] = useState(null);
  const [recommended, setRecommended] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [m, p] = await Promise.all([api.get("/measurements"), api.get("/profile")]);
    setDoc(m.data);
    setRecommended(p.data.profile?.recommended_measurements || ["poids", "tour_taille"]);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "" && v !== undefined).map(([k, v]) => [k, parseFloat(v)]));
    if (Object.keys(payload).length === 0) return toast.error("Renseigne au moins une mesure");
    setSaving(true);
    try { await api.post("/measurements", payload); setForm({}); await load(); toast.success("Relevé enregistré"); }
    catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  if (!doc) return <ClientLayout><div className="pulse-organic w-3 h-3 rounded-full bg-sage mx-auto mt-20" /></ClientLayout>;
  const entries = [...(doc.entries || [])].reverse();

  return (
    <ClientLayout>
      <div className="fade-up">
        <h1 className="text-3xl font-light tracking-tight">Mensurations</h1>
        <p className="mt-2 text-obsidian/50 text-sm">Un relevé toutes les ~4 semaines suffit. Inutile de mesurer chaque jour.</p>

        {recommended.length > 0 && (
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <ProvenanceBadge type="recommandation">SUGGÉRÉ PAR L'IA</ProvenanceBadge>
            {recommended.map((r) => <span key={r} className="text-xs rounded-full bg-secondary px-2.5 py-1">{r.replace("_", " ")}</span>)}
          </div>
        )}

        <div className="mt-6 rounded-xl border border-border bg-white p-6">
          <div className="text-xs tracking-[0.16em] uppercase font-semibold text-obsidian/50 mb-4">Nouveau relevé</div>
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-xs text-obsidian/50">{f.label}</label>
                <input data-testid={`meas-${f.key}`} type="number" value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="mt-1 w-full rounded-lg border border-border px-3 py-2.5 text-sm tabnum focus:ring-1 focus:ring-obsidian focus:outline-none" />
              </div>
            ))}
          </div>
          <button data-testid="meas-save" onClick={save} disabled={saving}
            className="mt-5 w-full rounded-full bg-obsidian text-white py-3.5 font-medium inline-flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-60">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Enregistrer le relevé
          </button>
        </div>

        <h2 className="mt-10 text-lg font-medium">Historique</h2>
        <div className="mt-3 space-y-2">
          {entries.length === 0 && <p className="text-sm text-obsidian/40">Aucun relevé pour le moment.</p>}
          {entries.map((e, i) => (
            <div key={i} className="rounded-xl border border-border bg-white p-4">
              <div className="text-xs text-obsidian/40">{new Date(e.date).toLocaleDateString("fr-FR")}</div>
              <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1">
                {FIELDS.filter((f) => e[f.key] !== undefined).map((f) => (
                  <span key={f.key} className="text-sm"><span className="text-obsidian/50">{f.label.split(" (")[0]}:</span> <span className="font-medium tabnum">{e[f.key]}</span></span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </ClientLayout>
  );
}
